// Markdown body for the post. Metadata lives in src/blog/posts.json.
const body = `
Look at the inside of the shank under a loupe. Hand fabricated metal shows file marks, faint burnishing and small asymmetries. Cast metal shows a slightly grainy, orange-peel surface, occasional round pits, and detail that looks softened as though seen through a thin film.

That difference is most of what separates a piece made by a jeweller at a bench from one poured into a mould, and it is worth knowing because it drives both value and repairability.

## The two ways a piece gets made

**Fabrication.** Metal is rolled, drawn, sawn, filed, bent and soldered together. Each part is made and then joined. This is how almost everything was made before the twentieth century, and how good bench work is still made today.

**Casting.** A model is made, a mould is taken from it, and molten metal is poured or spun into that mould. Lost wax casting has existed for millennia, but casting *jewelry in quantity* became normal in the twentieth century, and dominates the mass market now.

Neither is inherently better. A well finished cast piece can be excellent. But cast and fabricated pieces age, wear and repair differently, and on antique jewelry the distinction is also a dating clue.

## What to look for

| Sign | Fabricated | Cast |
| --- | --- | --- |
| Surface | File marks, burnishing, crisp planes | Faint orange peel or grain |
| Detail | Sharp, sometimes slightly uneven | Softened, rounded, uniform |
| Porosity | None | Occasional tiny round pits |
| Joins | Visible solder seams, faint colour change | None, the piece is one lump |
| Repeats | Each element slightly different | Identical repeats |
| Back | Finished, worked | Often flat and unworked |
| Claws | Individually made and set | Cast as part of the head |

## The seam test

On a fabricated piece, separate parts were joined. Look at the junction between a ring shank and its head, between a bail and a pendant, or between a brooch frame and its pin assembly. You are looking for a fine line where two pieces meet, sometimes with a whisper of different colour where the solder sits.

A cast piece has no seam there, because the shank and the head came out of the mould together.

Careful: a well polished fabricated join can be nearly invisible, and a cast piece can have a fabricated bail added. Use the seam as evidence, not proof.

## Porosity

Cast metal traps gas. Under magnification you may find tiny round pits, particularly in thicker sections and along edges. They look like the surface of a golf ball at very small scale.

Fabricated metal has no porosity. It may have scratches, dents and tool marks, but not round holes.

If a piece has been heavily polished, porosity is one of the few things polishing cannot hide, because polishing opens the pits rather than closing them.

## Symmetry, and why perfect is a warning sign

Look at a row of repeated elements: leaves on a spray brooch, links in a fancy chain, the scrollwork on a shoulder. Made by hand, each one is slightly different. There is a hand behind them.

Cast repeats are identical, because they came from the same mould. On a piece claiming to be Victorian, five identical leaves is a reason to look harder. Our [guide to spotting reproductions](/blog/how-to-spot-fake-antique-jewelry) covers what else to check.

## Weight where you do not expect it

Cast pieces are often made a little heavier than necessary, because thin sections do not fill reliably in a mould. Fabricated antique work is often surprisingly light and thin, because metal was expensive and the maker used exactly as much as the job needed.

An antique ring shank worn thin at the bottom after a century is normal. A modern cast copy of the same design will usually feel chunky by comparison.

## Why it matters for value

**Originality.** On antique jewelry, hand fabrication is what buyers expect. A cast copy of a period design is worth a fraction of the original, even in the same metal.

**Repairability.** Fabricated pieces can be taken apart at the joins and rebuilt. Cast pieces are one piece, and porosity makes soldering unpredictable: heat opens the pits and the repair can look worse than the damage. Ask a jeweller before assuming a cast piece can be resized.

**Durability.** Cast claws are usually thicker but more brittle. Fabricated claws are worked metal and bend rather than snap. This is why a cast setting can lose a stone from a knock that a fabricated one would survive.

## Where casting is normal and expected

Do not treat casting as a red flag on its own.

- **Retro and mid century pieces**, roughly 1935 onwards, are frequently cast and are genuine to their period.
- **Signet ring blanks** were cast and then hand engraved, which is exactly how they should be.
- **Some Art Nouveau work** was cast deliberately, because the flowing forms suited it.

The question is never simply cast or not. It is whether the method matches the period and the price.

## How to inspect one you cannot hold

Ask for two photographs: the back of the piece, and a close macro of the inside of the shank or the underside of the setting. Ask for daylight and no filters. Our [phone photography guide](/blog/how-to-photograph-jewelry-with-your-phone) explains how a seller can take these, and it is a reasonable thing to ask for.

Then ask directly: is it cast or hand made, and how do you know? A seller who says "I think cast, from the surface under the setting" is telling you something useful. A seller who has never looked will say so, and that is honest too.

On Nextmine the buyer's payment is held until the piece has arrived and been confirmed, so a piece that turns out to be a modern casting is recoverable rather than a lost transfer. [See what is listed](/s), or read [how payments work](/how-payments-work).
`;

export default body;
